/**
 * The media listing (spec 003). Two modes over one listing:
 *
 * - `library` (the /media screen): the folder follows the URL, multi-select,
 *   upload, rename, move by drag, download, copy link, delete (always through
 *   ConfirmDeleteModal), folder create / icon / delete.
 * - `picker` (MediaPicker): a read-only chooser. Folders navigate in local
 *   state, search / sort / type filter, upload into the folder being browsed,
 *   single selection; no delete, rename, move, folder ops, download or link.
 */
import { Alert, Badge, Button, Card, Col, Container, Dropdown, Form, InputGroup, Modal, ProgressBar, Row } from 'react-bootstrap';
import { FaCheckCircle, FaCopy, FaDownload, FaEye, FaSearch, FaSort, FaSortAlphaDown, FaSortAlphaUp, FaTrash, FaUpload } from 'react-icons/fa';
import { ArrowUp, Folder, FolderPlus, GripVertical, HouseFill, Image as ImageIcon, Pencil, Trash } from 'react-bootstrap-icons';
import { DragEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MediaTypeFilter, MediaFolder } from '@shared/@types';
import { MediaItem } from '../@types/Media';
import { apiClient } from '../api/client';
import { apiUrl } from '../Config';
import MediaPreview from './MediaPreview';
import GeneratedDetails from './imagegen/GeneratedDetails';
import { useModalContext } from '../contexts/ModalContext';
import MediaPicker from '../modals/MediaPicker';
import ConfirmDeleteModal, { ConfirmFolderDeleteModal } from './ConfirmDeleteModal';
import { useMediaUploads } from './media/useMediaUploads';
import {
  ACCEPTED_UPLOAD,
  FILTERS,
  KindFilter,
  SortBy,
  SortDirection,
  countByKind,
  downloadName,
  filterAndSortMedia,
  folderContents,
  folderOf,
  folderPath,
  isSelfOrDescendant,
  loadSort,
  saveSort,
  sortFoldersByName,
  formatMediaLength,
  hasMediaLength,
} from './media/mediaListing';

interface MediaFolderWithIcon extends MediaFolder {
  iconUrl?: string;
}

export interface MediaListProps {
  mode?: 'library' | 'picker';
  /** Initial type filter (and the only one when `lockFilter`). */
  typeFilter?: MediaTypeFilter;
  lockFilter?: boolean;
  /** Picker: the selected media changed (one id, or null), with its item. */
  onSelectionChange?: (id: number | null, item?: MediaItem) => void;
  /** Picker: a media was double-clicked; the item carries its length (spec 041). */
  onPick?: (id: number, item?: MediaItem) => void;
}

type DragPayload = { type: 'media' | 'folder'; ids: number[] };

type PendingDelete =
  | { kind: 'media'; ids: number[]; usedIn: string[] | null; iconOf: string[] }
  | { kind: 'folder'; folder: MediaFolderWithIcon };

const SORT_LABEL: Record<SortBy, string> = { name: 'Name', type: 'Type', newest: 'Newest first' };

/** An audio or video card's length beside its extension (spec 041); muted when unknown. */
function MediaLengthBadge({ durationMs }: { durationMs?: number }) {
  const text = formatMediaLength(durationMs);
  const unknown = text === 'length unknown';
  return (
    <span className={`me-1 ${unknown ? 'fst-italic text-muted' : 'text-body'}`} data-testid="media-length">
      {text}
    </span>
  );
}

/** Inline rename: Enter or blur saves, Escape cancels; saves once. */
function InlineRename({ value, onSave, onCancel }: { value: string; onSave: (name: string) => void; onCancel: () => void }) {
  const done = useRef(false);
  const finish = (name: string | null) => {
    if (done.current) return;
    done.current = true;
    if (name === null || !name.trim() || name.trim() === value) onCancel();
    else onSave(name.trim());
  };
  return (
    <Form.Control
      type="text"
      size="sm"
      defaultValue={value}
      autoFocus
      aria-label="New name"
      onFocus={(e) => e.currentTarget.select()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onBlur={(e) => finish(e.currentTarget.value)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') finish(e.currentTarget.value);
        else if (e.key === 'Escape') finish(null);
      }}
    />
  );
}

const MediaList = ({ mode = 'library', typeFilter, lockFilter = false, onSelectionChange, onPick }: MediaListProps) => {
  const isPicker = mode === 'picker';
  const navigate = useNavigate();
  const { folderId: folderIdParam } = useParams<{ folderId: string }>();
  const { addModal, removeModal } = useModalContext();

  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [folders, setFolders] = useState<MediaFolderWithIcon[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Folder: the URL in the library, local state in the picker.
  const [pickerFolderId, setPickerFolderId] = useState<number | null>(null);
  const urlFolderId = folderIdParam ? parseInt(folderIdParam, 10) : null;
  const currentFolderId = isPicker ? pickerFolderId : Number.isFinite(urlFolderId) ? urlFolderId : null;

  const [selectedMedia, setSelectedMedia] = useState<number[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<KindFilter>(typeFilter ?? 'all');
  const [{ sortBy, direction }, setSort] = useState(loadSort);

  const [editingFolderId, setEditingFolderId] = useState<number | null>(null);
  const [editingMediaId, setEditingMediaId] = useState<number | null>(null);
  const [dragged, setDragged] = useState<DragPayload | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [copyFallback, setCopyFallback] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragImageRef = useRef<HTMLDivElement | null>(null);
  const folderIconUrls = useRef<string[]>([]);

  // ─── Loading ──────────────────────────────────────────────────────────────

  const fetchFolders = useCallback(async (signal?: AbortSignal) => {
    try {
      const result = await apiClient.getMediaFolders({ signal });
      if (signal?.aborted) return;
      if (!result.success) {
        setError(result.error || 'Failed to load media folders');
        return;
      }
      const rows = (result.data || []) as MediaFolder[];
      const urls: string[] = [];
      const withIcons = await Promise.all(
        rows.map(async (folder) => {
          if (typeof folder.iconId !== 'number') return { ...folder, parentId: folder.parentId ?? null };
          try {
            const blob = await apiClient.getMediaFileBlob(folder.iconId, { signal });
            const iconUrl = URL.createObjectURL(blob);
            urls.push(iconUrl);
            return { ...folder, parentId: folder.parentId ?? null, iconUrl };
          } catch {
            return { ...folder, parentId: folder.parentId ?? null };
          }
        })
      );
      if (signal?.aborted) {
        urls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      folderIconUrls.current.forEach((url) => URL.revokeObjectURL(url));
      folderIconUrls.current = urls;
      setFolders(withIcons);
    } catch (e) {
      if (!signal?.aborted) setError('Failed to load media folders');
    }
  }, []);

  const fetchMedia = useCallback(async (signal?: AbortSignal) => {
    try {
      const result = await apiClient.getMedia({ signal });
      if (signal?.aborted) return;
      if (!result.success) {
        setError(result.error || 'Failed to load media files');
        return;
      }
      setMediaItems(((result.data || []) as MediaItem[]).map((m) => ({ ...m, folderId: m.folderId ?? null })));
    } catch {
      if (!signal?.aborted) setError('Failed to load media files');
    }
  }, []);

  const refresh = useCallback(() => Promise.all([fetchMedia(), fetchFolders()]), [fetchMedia, fetchFolders]);

  // Initial load; under StrictMode the first run is aborted and discarded.
  useEffect(() => {
    const controller = new AbortController();
    fetchMedia(controller.signal);
    fetchFolders(controller.signal);
    return () => controller.abort();
  }, [fetchMedia, fetchFolders]);

  useEffect(
    () => () => {
      folderIconUrls.current.forEach((url) => URL.revokeObjectURL(url));
      folderIconUrls.current = [];
    },
    []
  );

  // ─── Derived listing ──────────────────────────────────────────────────────

  const currentFolder = folders.find((f) => f.id === currentFolderId);
  const breadcrumb = useMemo(() => folderPath(folders, currentFolderId), [folders, currentFolderId]);
  const folderMedia = useMemo(
    () => mediaItems.filter((m) => folderOf(m) === currentFolderId),
    [mediaItems, currentFolderId]
  );
  const counts = useMemo(() => countByKind(folderMedia), [folderMedia]);
  const visibleMedia = useMemo(
    () => filterAndSortMedia(folderMedia, { search, kind, sortBy, direction }),
    [folderMedia, search, kind, sortBy, direction]
  );
  const visibleFolders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortFoldersByName(
      folders.filter((f) => (f.parentId ?? null) === currentFolderId && (!q || f.name.toLowerCase().includes(q)))
    );
  }, [folders, currentFolderId, search]);

  // ─── Navigation and selection ─────────────────────────────────────────────

  const goTo = useCallback(
    (folderId: number | null) => {
      setSelectedMedia([]);
      setSelectedFolders([]);
      if (isPicker) setPickerFolderId(folderId);
      else navigate(folderId === null ? '/media' : `/media/folder/${folderId}`);
    },
    [isPicker, navigate]
  );

  // Selection resets when the library's URL folder changes.
  useEffect(() => {
    if (!isPicker) {
      setSelectedMedia([]);
      setSelectedFolders([]);
    }
  }, [isPicker, folderIdParam]);

  useEffect(() => {
    if (!isPicker) return;
    const id = selectedMedia[0] ?? null;
    onSelectionChange?.(id, id === null ? undefined : mediaItems.find((m) => m.id === id));
  }, [isPicker, onSelectionChange, selectedMedia, mediaItems]);

  const toggle = (list: number[], id: number) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const clickMedia = (id: number) => {
    if (isPicker) setSelectedMedia([id]);
    else setSelectedMedia((prev) => toggle(prev, id));
  };

  const clickFolder = (id: number) => {
    if (!isPicker) setSelectedFolders((prev) => toggle(prev, id));
  };

  const setSortBy = (next: SortBy) => {
    setSort({ sortBy: next, direction });
    saveSort(next, direction);
  };
  const flipDirection = () => {
    const next: SortDirection = direction === 'asc' ? 'desc' : 'asc';
    setSort({ sortBy, direction: next });
    saveSort(sortBy, next);
  };

  // ─── Upload ───────────────────────────────────────────────────────────────

  const { rows: uploadRows, upload, dismiss } = useMediaUploads(
    useCallback((item: MediaItem) => {
      setMediaItems((prev) => [...prev.filter((m) => m.id !== item.id), { ...item, folderId: item.folderId ?? null }]);
    }, [])
  );

  const uploadFiles = (files: FileList | File[]) => {
    if (files.length > 0) upload(files, currentFolderId);
  };

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

  // ─── Rename ───────────────────────────────────────────────────────────────

  const renameMedia = async (item: MediaItem, name: string) => {
    setEditingMediaId(null);
    const old = item.name;
    setMediaItems((prev) => prev.map((m) => (m.id === item.id ? { ...m, name } : m)));
    try {
      const res = await apiClient.updateMedia(item.id, { name });
      if (!res.success) throw new Error(res.error || 'Rename failed');
    } catch (e) {
      setMediaItems((prev) => prev.map((m) => (m.id === item.id ? { ...m, name: old } : m)));
      setError(`Could not rename "${old}": ${e instanceof Error ? e.message : 'Rename failed'}`);
    }
  };

  const renameFolder = async (folder: MediaFolderWithIcon, name: string) => {
    setEditingFolderId(null);
    const old = folder.name;
    setFolders((prev) => prev.map((f) => (f.id === folder.id ? { ...f, name } : f)));
    try {
      const res = await apiClient.updateMediaFolder(folder.id, { name });
      if (!res.success) throw new Error(res.error || 'Rename failed');
    } catch (e) {
      setFolders((prev) => prev.map((f) => (f.id === folder.id ? { ...f, name: old } : f)));
      setError(`Could not rename "${old}": ${e instanceof Error ? e.message : 'Rename failed'}`);
    }
  };

  // ─── Folder create / icon ─────────────────────────────────────────────────

  const createFolder = async () => {
    const names = new Set(folders.filter((f) => (f.parentId ?? null) === currentFolderId).map((f) => f.name));
    let name = 'New Folder';
    for (let n = 2; names.has(name); n++) name = `New Folder ${n}`;
    const res = await apiClient.createMediaFolder({ name, parentId: currentFolderId }).catch(() => null);
    if (!res?.success) {
      setError(res?.error || 'Failed to create folder');
      return;
    }
    await fetchFolders();
    if (res.data?.id) setEditingFolderId(res.data.id);
  };

  const chooseFolderIcon = (folderId: number) => {
    addModal({
      title: 'Select Folder Icon',
      size: 'xl',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={async (mediaId) => {
            const res = await apiClient.updateMediaFolder(folderId, { iconId: mediaId }).catch(() => null);
            removeModal();
            if (!res?.success) setError(res?.error || 'Failed to set the folder icon');
            await fetchFolders();
          }}
          onCancel={() => removeModal()}
        />
      ),
    });
  };

  const removeFolderIcon = async (folderId: number) => {
    const res = await apiClient.updateMediaFolder(folderId, { iconId: null }).catch(() => null);
    if (!res?.success) setError(res?.error || 'Failed to remove the folder icon');
    await fetchFolders();
  };

  // ─── Move (drag onto a folder, a breadcrumb segment or "Go up") ───────────

  const startDrag = (e: DragEvent, type: 'media' | 'folder', id: number) => {
    const selection = type === 'media' ? selectedMedia : selectedFolders;
    const ids = selection.includes(id) && selection.length > 1 ? [...selection] : [id];
    setDragged({ type, ids });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-storymotion-media', JSON.stringify({ type, ids }));

    if (ids.length > 1) {
      const badge = document.createElement('div');
      badge.textContent = `${ids.length} items`;
      badge.style.cssText =
        'position:absolute;top:-1000px;left:-1000px;padding:10px 14px;border-radius:8px;background:#0d6efd;color:#fff;font-weight:600;font-size:14px;';
      document.body.appendChild(badge);
      dragImageRef.current = badge;
      e.dataTransfer.setDragImage(badge, 20, 20);
    }
  };

  const endDrag = () => {
    setDragged(null);
    setDropTarget(null);
    if (dragImageRef.current) {
      dragImageRef.current.remove();
      dragImageRef.current = null;
    }
  };

  /** A folder cannot go into itself or one of its descendants; media cannot "move" to where it is. */
  const canDrop = (payload: DragPayload | null, target: number | null) => {
    if (!payload) return false;
    if (payload.type === 'folder') return payload.ids.every((id) => !isSelfOrDescendant(folders, id, target));
    return true;
  };

  const dropProps = (target: number | null, key: string) =>
    isPicker
      ? {}
      : {
          onDragOver: (e: DragEvent) => {
            if (!dragged) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = canDrop(dragged, target) ? 'move' : 'none';
            setDropTarget(canDrop(dragged, target) ? key : null);
          },
          onDragLeave: () => setDropTarget((prev) => (prev === key ? null : prev)),
          onDrop: (e: DragEvent) => {
            if (!dragged) return; // files dropped from the desktop: the list uploads them
            e.preventDefault();
            e.stopPropagation();
            const payload = dragged;
            endDrag();
            if (canDrop(payload, target)) moveTo(payload, target);
          },
        };

  const moveTo = async (payload: DragPayload, target: number | null) => {
    const results = await Promise.all(
      payload.ids.map((id) =>
        (payload.type === 'media'
          ? apiClient.updateMedia(id, { folderId: target })
          : apiClient.updateMediaFolder(id, { parentId: target })
        ).catch((e: Error) => ({ success: false as const, error: e.message }))
      )
    );
    const failed = results.filter((r) => !r.success);
    if (failed.length > 0) setError(`Could not move ${failed.length} item(s): ${failed[0].error}`);
    if (payload.type === 'media') setSelectedMedia([]);
    else setSelectedFolders([]);
    await refresh();
  };

  // ─── Download / copy link ─────────────────────────────────────────────────

  const download = async (item: MediaItem) => {
    try {
      const blob = await apiClient.getMediaFileBlob(item.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName(item);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(`Download failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  };

  const copyLink = async (item: MediaItem) => {
    const url = `${apiUrl.replace(/\/$/, '')}/media/${item.id}/preview`;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      setNotice('Link copied');
      window.setTimeout(() => setNotice((n) => (n === 'Link copied' ? null : n)), 2500);
    } catch {
      setCopyFallback(url);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────

  const askDeleteMedia = async (ids: number[]) => {
    setPendingDelete({ kind: 'media', ids, usedIn: null, iconOf: [] });
    const usages = await Promise.all(ids.map((id) => apiClient.getMediaUsage(id).catch(() => null)));
    const scenes = new Map<number, string>();
    const icons: string[] = [];
    for (const usage of usages) {
      if (!usage?.success) continue;
      usage.data.scenes.forEach((s) => scenes.set(s.id, s.name));
      usage.data.folders.forEach((f) => icons.push(f.name));
    }
    setPendingDelete((prev) =>
      prev && prev.kind === 'media' && prev.ids === ids ? { ...prev, usedIn: [...scenes.values()], iconOf: icons } : prev
    );
  };

  const deleteMedia = async (ids: number[]) => {
    const results = await Promise.all(
      ids.map((id) => apiClient.deleteMedia(id).catch((e: Error) => ({ success: false as const, error: e.message })))
    );
    setSelectedMedia((prev) => prev.filter((id) => !ids.includes(id)));
    await refresh();
    const failed = results.filter((r) => !r.success);
    if (failed.length > 0) throw new Error(`${failed.length} item(s) could not be deleted: ${failed[0].error}`);
  };

  const deleteFolder = async (folder: MediaFolderWithIcon, deleteContents: boolean) => {
    const res = await apiClient.deleteMediaFolder(folder.id, { deleteContents });
    if (!res.success) throw new Error(res.error || 'Failed to delete the folder');
    if (currentFolderId !== null && isSelfOrDescendant(folders, folder.id, currentFolderId)) {
      goTo(folder.parentId ?? null);
    }
    setSelectedFolders((prev) => prev.filter((id) => id !== folder.id));
    await refresh();
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const itemName = (id: number) => mediaItems.find((m) => m.id === id)?.name ?? `#${id}`;
  const goUpTarget = currentFolder?.parentId ?? null;

  return (
    <Container
      fluid
      className={`${isPicker ? 'p-2' : 'p-4'} d-flex flex-column h-100 ${isFileDragOver ? 'bg-light' : ''}`}
      data-testid={isPicker ? 'media-picker' : 'media-library'}
      onDragOver={(e) => {
        if (hasFiles(e) && !dragged) {
          e.preventDefault();
          setIsFileDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setIsFileDragOver(false);
      }}
      onDrop={(e) => {
        setIsFileDragOver(false);
        if (!dragged && e.dataTransfer.files?.length) {
          e.preventDefault();
          uploadFiles(e.dataTransfer.files);
        }
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        accept={ACCEPTED_UPLOAD}
        data-testid="media-upload-input"
        onChange={(e) => {
          if (e.target.files) uploadFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <div className={isPicker ? 'pb-2' : 'p-4 sticky-top bg-white border-bottom'} style={isPicker ? undefined : { top: 0, zIndex: 1020 }}>
        <nav aria-label="Media folders">
          <ol className="breadcrumb mb-3">
            <li
              className={`breadcrumb-item ${currentFolderId === null ? 'active' : ''} ${dropTarget === 'crumb-root' ? 'bg-primary-subtle rounded' : ''}`}
              data-testid="breadcrumb-root"
              {...dropProps(null, 'crumb-root')}
            >
              {currentFolderId === null ? (
                <span>
                  <HouseFill className="me-1" /> Media
                </span>
              ) : (
                <a href="#" onClick={(e) => { e.preventDefault(); goTo(null); }}>
                  <HouseFill className="me-1" /> Media
                </a>
              )}
            </li>
            {breadcrumb.map((folder, index) => {
              const last = index === breadcrumb.length - 1;
              const key = `crumb-${folder.id}`;
              return (
                <li
                  key={folder.id}
                  className={`breadcrumb-item ${last ? 'active' : ''} ${dropTarget === key ? 'bg-primary-subtle rounded' : ''}`}
                  data-testid="breadcrumb-folder"
                  {...(last ? {} : dropProps(folder.id, key))}
                >
                  {last ? (
                    folder.name
                  ) : (
                    <a href="#" onClick={(e) => { e.preventDefault(); goTo(folder.id); }}>
                      {folder.name}
                    </a>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert variant="success" className="mb-3 py-2" role="status">
            {notice}
          </Alert>
        )}

        {uploadRows.length > 0 && (
          <div className="mb-3" data-testid="upload-rows">
            {uploadRows.map((row) => (
              <div key={row.key} className="mb-2" data-testid="upload-row" data-status={row.status}>
                <div className="d-flex justify-content-between align-items-center">
                  <small>{row.name}</small>
                  {row.status === 'error' && (
                    <Button variant="link" size="sm" className="p-0" onClick={() => dismiss(row.key)} aria-label={`Dismiss ${row.name}`}>
                      Dismiss
                    </Button>
                  )}
                </div>
                {row.status === 'error' ? (
                  <small className="text-danger d-block" role="alert">
                    {row.error}
                  </small>
                ) : (
                  <ProgressBar
                    now={row.progress}
                    label={`${row.progress}%`}
                    visuallyHidden
                    variant={row.status === 'done' ? 'success' : 'primary'}
                    className="mt-1"
                    aria-label={`Uploading ${row.name}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="primary" onClick={() => fileInputRef.current?.click()}>
              <FaUpload className="me-2" />
              Upload
            </Button>
            {!isPicker && (
              <>
                <Button variant="outline-primary" onClick={createFolder}>
                  <FolderPlus className="me-2" />
                  New Folder
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={() => askDeleteMedia([...selectedMedia])}
                  disabled={selectedMedia.length === 0}
                >
                  <FaTrash className="me-2" />
                  Delete selected ({selectedMedia.length})
                </Button>
              </>
            )}
          </div>
          <div className="d-flex gap-2">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm" aria-label="Sort">
                <FaSort className="me-2" />
                Sort: {SORT_LABEL[sortBy]}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {(Object.keys(SORT_LABEL) as SortBy[]).map((key) => (
                  <Dropdown.Item key={key} onClick={() => setSortBy(key)} active={sortBy === key}>
                    {SORT_LABEL[key]}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            {sortBy !== 'newest' && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={flipDirection}
                title={direction === 'asc' ? 'Sort descending' : 'Sort ascending'}
                aria-label={direction === 'asc' ? 'Sort descending' : 'Sort ascending'}
              >
                {direction === 'asc' ? <FaSortAlphaUp /> : <FaSortAlphaDown />}
              </Button>
            )}
          </div>
        </div>

        <div className="d-flex gap-3 flex-wrap align-items-center">
          <InputGroup style={{ minWidth: 200, maxWidth: 300 }}>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search this folder…"
              aria-label="Search media"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <div className="d-flex gap-1 flex-wrap" role="group" aria-label="Type filter">
            {FILTERS.map(({ key, label }) => (
              <Button
                key={key}
                variant={kind === key ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setKind(key)}
                disabled={lockFilter && kind !== key}
                aria-pressed={kind === key}
                data-testid={`filter-${key}`}
                className="d-flex align-items-center gap-1"
              >
                {label}
                <Badge bg={kind === key ? 'light' : 'primary'} text={kind === key ? 'dark' : 'light'} data-testid={`count-${key}`}>
                  {counts[key]}
                </Badge>
              </Button>
            ))}
          </div>
          {!isPicker && (
            <small className="ms-auto text-muted">
              <FaUpload className="me-1" /> Drop files anywhere to upload
            </small>
          )}
        </div>
      </div>

      <div className="flex-grow-1 overflow-auto pt-3" style={isPicker ? { maxHeight: '55vh' } : undefined}>
        {currentFolderId !== null && (
          <Row className="g-3 mb-3">
            <Col xs={12} sm={6} md={4} lg={3} xl={2}>
              <Card
                className={`h-100 ${dropTarget === 'go-up' ? 'border-primary bg-light' : 'border-light'}`}
                style={{ cursor: 'pointer', minHeight: isPicker ? 110 : 150 }}
                data-testid="go-up"
                onClick={() => goTo(goUpTarget)}
                {...dropProps(goUpTarget, 'go-up')}
              >
                <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center">
                  <ArrowUp size={40} className="text-muted mb-2" />
                  <Card.Title className="h6 mb-0">Go up</Card.Title>
                  <small className="text-muted">{goUpTarget !== null ? 'Parent folder' : 'Media'}</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {visibleMedia.length === 0 && visibleFolders.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <FaUpload size={48} className="mb-3 opacity-50" />
            <h5>No files here</h5>
            <p>Upload some files or change the search or filter.</p>
          </div>
        ) : (
          <Row className="g-3">
            {visibleFolders.map((folder) => {
              const selected = selectedFolders.includes(folder.id);
              const isDragged = dragged?.type === 'folder' && dragged.ids.includes(folder.id);
              const key = `folder-${folder.id}`;
              return (
                <Col key={key} xs={12} sm={6} md={4} lg={3} xl={2}>
                  <Card
                    className={`h-100 ${selected ? 'border-primary shadow' : dropTarget === key ? 'border-primary bg-light' : 'border-light'} ${isDragged ? 'opacity-50' : ''}`}
                    style={{ cursor: 'pointer', minHeight: isPicker ? 110 : 150 }}
                    data-testid="folder-card"
                    aria-label={`Folder ${folder.name}`}
                    draggable={!isPicker}
                    onDragStart={isPicker ? undefined : (e) => startDrag(e, 'folder', folder.id)}
                    onDragEnd={isPicker ? undefined : endDrag}
                    {...dropProps(folder.id, key)}
                    onClick={() => clickFolder(folder.id)}
                    onDoubleClick={() => editingFolderId !== folder.id && goTo(folder.id)}
                  >
                    <Card.Body className="d-flex flex-column align-items-center text-center position-relative">
                      {selected && (
                        <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 10 }}>
                          <FaCheckCircle size={22} color="#0d6efd" />
                        </div>
                      )}
                      {!isPicker && (
                        <>
                          <div className="position-absolute top-0 start-0 p-2" style={{ cursor: 'grab' }}>
                            <GripVertical size={16} className="text-muted" />
                          </div>
                          <Dropdown className={`position-absolute top-0 end-0 ${selected ? 'me-4' : ''}`} onClick={(e) => e.stopPropagation()}>
                            <Dropdown.Toggle variant="link" size="sm" className="text-muted p-1" aria-label={`Folder actions for ${folder.name}`}>
                              ⋮
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => setEditingFolderId(folder.id)}>
                                <Pencil className="me-2" /> Rename
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => chooseFolderIcon(folder.id)}>
                                <ImageIcon className="me-2" /> Set icon
                              </Dropdown.Item>
                              {typeof folder.iconId === 'number' && (
                                <Dropdown.Item onClick={() => removeFolderIcon(folder.id)}>
                                  <ImageIcon className="me-2" /> Remove icon
                                </Dropdown.Item>
                              )}
                              <Dropdown.Divider />
                              <Dropdown.Item className="text-danger" onClick={() => setPendingDelete({ kind: 'folder', folder })}>
                                <Trash className="me-2" /> Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </>
                      )}
                      <div className="mb-2 d-flex align-items-center justify-content-center" style={{ width: '100%', height: isPicker ? 70 : 110 }}>
                        {folder.iconUrl ? (
                          <img src={folder.iconUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
                        ) : (
                          <Folder size={isPicker ? 56 : 80} className="text-warning" />
                        )}
                      </div>
                      {editingFolderId === folder.id ? (
                        <InlineRename value={folder.name} onSave={(name) => renameFolder(folder, name)} onCancel={() => setEditingFolderId(null)} />
                      ) : (
                        <Card.Title className="h6 mb-0 text-truncate w-100" title={folder.name}>
                          {folder.name}
                        </Card.Title>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}

            {visibleMedia.map((item) => {
              const selected = selectedMedia.includes(item.id);
              const isDragged = dragged?.type === 'media' && dragged.ids.includes(item.id);
              return (
                <Col key={`media-${item.id}`} xs={12} sm={6} md={4} lg={3} xl={2}>
                  <Card
                    className={`h-100 position-relative ${selected ? 'border-primary shadow' : 'border-light'} ${isDragged ? 'opacity-50' : ''}`}
                    style={{ cursor: 'pointer' }}
                    data-testid="media-card"
                    data-media-id={item.id}
                    aria-label={item.name}
                    aria-selected={selected}
                    draggable={!isPicker}
                    onDragStart={isPicker ? undefined : (e) => startDrag(e, 'media', item.id)}
                    onDragEnd={isPicker ? undefined : endDrag}
                    onClick={() => clickMedia(item.id)}
                    onDoubleClick={() => {
                      if (isPicker) onPick?.(item.id, item);
                    }}
                  >
                    {selected && (
                      <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 10 }}>
                        <FaCheckCircle size={22} color="#0d6efd" />
                      </div>
                    )}
                    {!isPicker && (
                      <div className="position-absolute top-0 start-0 p-2" style={{ cursor: 'grab' }}>
                        <GripVertical size={16} className="text-muted" />
                      </div>
                    )}
                    <Card.Body className="d-flex flex-column p-3">
                      <div className="text-center mb-2 d-flex align-items-center justify-content-center" style={{ height: isPicker ? 90 : 120 }}>
                        <MediaPreview item={item} width="100%" height="100%" />
                      </div>
                      {editingMediaId === item.id ? (
                        <InlineRename value={item.name} onSave={(name) => renameMedia(item, name)} onCancel={() => setEditingMediaId(null)} />
                      ) : (
                        <Card.Title className="h6 mb-1 text-truncate" title={item.name}>
                          {item.name}
                        </Card.Title>
                      )}
                      <div className="text-muted small mb-2">
                        <Badge bg="secondary" className="me-1">
                          {(item.extension || '?').toUpperCase()}
                        </Badge>
                        {hasMediaLength(item.type) && (
                          <MediaLengthBadge durationMs={item.durationMs} />
                        )}
                        {item.generated && (
                          <Badge bg="info" className="me-1" data-testid="media-generated-badge">
                            GENERATED
                          </Badge>
                        )}
                        <span>{item.type.split('/')[0]}</span>
                      </div>
                      <div className="mt-auto d-flex gap-1 flex-wrap">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Preview"
                          aria-label={`Preview ${item.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreview(item);
                          }}
                        >
                          <FaEye size={12} />
                        </Button>
                        {!isPicker && (
                          <>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              title="Rename"
                              aria-label={`Rename ${item.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingMediaId(item.id);
                              }}
                            >
                              <Pencil size={12} />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              title="Copy link"
                              aria-label={`Copy link to ${item.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyLink(item);
                              }}
                            >
                              <FaCopy size={12} />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              title="Download"
                              aria-label={`Download ${item.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                download(item);
                              }}
                            >
                              <FaDownload size={12} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              title="Delete"
                              aria-label={`Delete ${item.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                askDeleteMedia([item.id]);
                              }}
                            >
                              <FaTrash size={12} />
                            </Button>
                          </>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      <Modal show={!!preview} onHide={() => setPreview(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{preview?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {preview && (
            <>
              <MediaPreview item={preview} width="100%" height="400px" />
              <p className="mt-3 text-muted mb-0">
                {preview.type} · .{preview.extension}
                {hasMediaLength(preview.type) && <span data-testid="media-preview-length"> · {formatMediaLength(preview.durationMs)}</span>}
              </p>
              {preview.generated && <GeneratedDetails mediaId={preview.id} />}
            </>
          )}
        </Modal.Body>
      </Modal>

      {!isPicker && (
        <>
          <ConfirmDeleteModal
            show={pendingDelete?.kind === 'media'}
            title={
              pendingDelete?.kind === 'media'
                ? `Delete ${pendingDelete.ids.length} item${pendingDelete.ids.length === 1 ? '' : 's'}?`
                : ''
            }
            checking={pendingDelete?.kind === 'media' && pendingDelete.usedIn === null}
            usedIn={pendingDelete?.kind === 'media' ? pendingDelete.usedIn ?? undefined : undefined}
            usedInWarning="Those scenes will show a missing image or play no sound where this media was used."
            onClose={() => setPendingDelete(null)}
            actions={[
              {
                key: 'delete',
                label: 'Delete',
                variant: 'danger',
                isDefault: true,
                run: () => (pendingDelete?.kind === 'media' ? deleteMedia(pendingDelete.ids) : undefined),
              },
            ]}
          >
            {pendingDelete?.kind === 'media' && (
              <>
                <p className="mb-1">
                  {pendingDelete.ids.length === 1
                    ? `"${itemName(pendingDelete.ids[0])}" will be removed for good.`
                    : `${pendingDelete.ids.length} items will be removed for good.`}
                </p>
                {pendingDelete.iconOf.length > 0 && (
                  <p className="small text-muted mb-0">Folder icon of: {pendingDelete.iconOf.join(', ')}</p>
                )}
              </>
            )}
          </ConfirmDeleteModal>

          <ConfirmFolderDeleteModal
            show={pendingDelete?.kind === 'folder'}
            folderName={pendingDelete?.kind === 'folder' ? pendingDelete.folder.name : ''}
            contents={(() => {
              if (pendingDelete?.kind !== 'folder') return { folders: 0, items: 0 };
              const c = folderContents(folders, mediaItems, pendingDelete.folder.id);
              return { folders: c.folders, items: c.media };
            })()}
            onMoveContentsUp={() => (pendingDelete?.kind === 'folder' ? deleteFolder(pendingDelete.folder, false) : undefined)}
            onDeleteEverything={() => (pendingDelete?.kind === 'folder' ? deleteFolder(pendingDelete.folder, true) : undefined)}
            onClose={() => setPendingDelete(null)}
          />

          <Modal show={copyFallback !== null} onHide={() => setCopyFallback(null)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Copy link</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="small text-muted">The clipboard is not available here. Copy the link by hand:</p>
              <Form.Control readOnly value={copyFallback ?? ''} aria-label="Media link" onFocus={(e) => e.currentTarget.select()} autoFocus />
            </Modal.Body>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default MediaList;
